/**
 * Cache en memoria de catalogos con TTL + invalidacion manual.
 *
 * Filosofia:
 *  - Catalogos = listas que cambian poco (tipos de servicio, personal,
 *    costos fijos, clientes, etc.) y se piden multiples veces por sesion.
 *  - TTL 5 min por defecto → cambios de otros usuarios aparecen al expirar.
 *  - Invalidacion manual → cuando vos mismo modificas algo, limpiamos
 *    inmediatamente para que la proxima lectura sea fresh.
 *
 * Uso:
 *   import { cached, invalidate } from './catalogCache';
 *   const data = await cached('tipos-servicio', () => tiposServicioAPI.getAll());
 *
 *   // Tras crear/editar/borrar:
 *   invalidate('tipos-servicio');
 *
 * El cache es a nivel modulo (singleton): se pierde al recargar la pagina,
 * persiste mientras el SPA este activo.
 */

const TTL_MS = 5 * 60 * 1000; // 5 minutos

// Map<key, { promise: Promise<any>, fetchedAt: number }>
// Guardamos la PROMESA (no el valor) para que requests concurrentes a la misma
// key compartan el mismo fetch (evita N requests paralelas para el mismo dato).
const store = new Map();

// Sincronizacion cross-tab: cuando una tab invalida keys, todas las tabs
// las tiran del cache. Sin esto, editar un CF en /costos-fijos no se reflejaba
// hasta 5 min despues en la tab de /cotizaciones/nueva.
// BroadcastChannel esta disponible en todos los navegadores modernos.
// Fallback a `storage` event via localStorage si no hay BroadcastChannel.
const CHANNEL_NAME = 'catalog-cache-sync';
let broadcastChannel = null;
try {
    if (typeof BroadcastChannel !== 'undefined') {
        broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
        broadcastChannel.onmessage = (evt) => {
            const keys = evt?.data?.keys;
            if (Array.isArray(keys)) keys.forEach(k => store.delete(k));
            else if (evt?.data?.all) store.clear();
        };
    }
} catch (_) { /* noop */ }

const notifyOtherTabs = (payload) => {
    try {
        if (broadcastChannel) {
            broadcastChannel.postMessage(payload);
        } else if (typeof window !== 'undefined' && window.localStorage) {
            // Fallback storage event: escribimos y borramos rapido, otras tabs
            // reciben el `storage` event y limpian su cache.
            const key = '__catalog_cache_invalidate__';
            window.localStorage.setItem(key, JSON.stringify({ ...payload, ts: Date.now() }));
            window.localStorage.removeItem(key);
        }
    } catch (_) { /* noop */ }
};

// Escuchar storage event como fallback si BroadcastChannel no esta disponible.
if (!broadcastChannel && typeof window !== 'undefined') {
    window.addEventListener('storage', (evt) => {
        if (evt.key !== '__catalog_cache_invalidate__' || !evt.newValue) return;
        try {
            const payload = JSON.parse(evt.newValue);
            if (Array.isArray(payload?.keys)) payload.keys.forEach(k => store.delete(k));
            else if (payload?.all) store.clear();
        } catch (_) { /* noop */ }
    });
}

/**
 * Obtiene del cache si esta fresh (< TTL), sino llama fetcher y cachea.
 * Las requests simultaneas a la misma key comparten la promesa (no se duplican).
 */
export async function cached(key, fetcher, { ttl = TTL_MS } = {}) {
    const entry = store.get(key);
    const now = Date.now();

    if (entry && (now - entry.fetchedAt) < ttl) {
        return entry.promise;
    }

    // Fresh fetch. Guardamos la promesa antes de await para deduplicacion.
    const promise = fetcher().catch(err => {
        // Si la fetch falla, no dejamos la promesa rota en el cache.
        store.delete(key);
        throw err;
    });

    store.set(key, { promise, fetchedAt: now });
    return promise;
}

/**
 * Invalida una key especifica. Proxima lectura ira a la API.
 * Tambien notifica a otras tabs para evitar datos viejos cross-tab.
 */
export function invalidate(key) {
    store.delete(key);
    notifyOtherTabs({ keys: [key] });
}

/**
 * Invalida varias keys de una. Util cuando una mutacion afecta varios cat.
 *   invalidateMany(['tipos-servicio', 'tipos-servicio-grouped'])
 */
export function invalidateMany(keys) {
    keys.forEach(k => store.delete(k));
    notifyOtherTabs({ keys });
}

/**
 * Limpia todo el cache. Util para logout o cambio drastico de contexto.
 */
export function invalidateAll() {
    store.clear();
    notifyOtherTabs({ all: true });
}

/**
 * Lectura sin TTL: si hay algo en cache lo devuelve, sino fetch.
 * Util si querias trabajar con datos potencialmente viejos sin esperar.
 */
export function cachedAny(key, fetcher) {
    return cached(key, fetcher, { ttl: Infinity });
}
